/**
 * 租戶設定作業 API Service（Task-tenant-provisioning PR F）
 * axios 攔截器自動帶 txn_token / Bearer，Service 層不需手動傳。
 */
import axiosInstance from '../api/axios';

const BASE = '/api/system/tenants';
const CFG = '/api/system/traefik-config';

export interface TenantListItem {
  org_id: number;
  org_code: string;
  org_name: string;
  primary_domain?: string | null;
  tenant_type: string;
  is_active: boolean;
}

export interface LoginPolicy {
  policy_code: string;
  factors: string[];
}

export interface TenantSettings {
  org_id: number;
  org_code: string;
  org_name: string;
  primary_domain?: string | null;
  alias_domains: string[];
  tenant_type: string;
  tenant_channels: string[];
  storage_quota_gb: number;
  home_index_path?: string | null;
  logo_file_id?: number | null;
  login_bg_file_id?: number | null;
  mail_domain?: string | null;
  tenant_title: Record<string, string>;
  tenant_copyright: Record<string, string>;
  tenant_languages: string[];
  allowed_role_ids: number[];   // Layer 1 · 系統管理員授權可用的 role_ids
  is_service_active: boolean;   // 租戶服務啟動開關（FALSE → 該 tenant 靜態暫停頁）
  office_enabled: boolean;      // Office 服務開關（Task-33 Phase 3 · 預設 FALSE）
  login_policy: LoginPolicy;
}

export interface TenantSettingsUpdate {
  primary_domain?: string | null;
  alias_domains?: string[];
  tenant_type?: string;
  tenant_channels?: string[];
  storage_quota_gb?: number;
  home_index_path?: string | null;
  mail_domain?: string | null;
  tenant_title?: Record<string, string>;
  tenant_copyright?: Record<string, string>;
  tenant_languages?: string[];
  allowed_role_ids?: number[];
  is_service_active?: boolean;
  office_enabled?: boolean;
  login_policy_code?: string;
  login_factors?: string[];
}

export interface MailDomainItem {
  domain_code: string;
  tenant_enabled: boolean;
  admin_only: boolean;
}

// 4-pre-c：TenantAiProvider / TenantAiProviderUpdate + getAiProvider/putAiProvider 已移除
// （AI Provider 設定改走 /ee_ai_providers）；保留 downloadAiBilling（用量帳單）。

export const listTenants = async (): Promise<TenantListItem[]> =>
  (await axiosInstance.get<TenantListItem[]>(BASE)).data;

export const getSettings = async (orgId: number): Promise<TenantSettings> =>
  (await axiosInstance.get<TenantSettings>(`${BASE}/${orgId}/settings`)).data;

export const updateSettings = async (orgId: number, data: TenantSettingsUpdate): Promise<TenantSettings> =>
  (await axiosInstance.patch<TenantSettings>(`${BASE}/${orgId}/settings`, data)).data;

export const listMailDomains = async (): Promise<MailDomainItem[]> =>
  (await axiosInstance.get<MailDomainItem[]>(`${BASE}/mail-domains`)).data;

// ── 進階操作（PR C/D/E 端點）──
export const generateConfig = async (orgId: number) =>
  (await axiosInstance.post(`${CFG}/generate/${orgId}`)).data;

export const applyConfig = async (orgId: number) =>
  (await axiosInstance.post(`${CFG}/apply/${orgId}`)).data;

export const dnsSync = async (orgId: number) =>
  (await axiosInstance.post(`${BASE}/${orgId}/dns-sync`)).data;

export const mailProvision = async (orgId: number) =>
  (await axiosInstance.post(`${BASE}/${orgId}/mail-provision`)).data;

// 回傳 CSV blob（呼叫端觸發下載）· 4-pre-c 保留
export const downloadAiBilling = async (orgId: number, month: string): Promise<Blob> =>
  (await axiosInstance.get(`${BASE}/${orgId}/ai-billing`, {
    params: { month },
    responseType: 'blob',
  })).data;
